
"use client";
import React from "react";
import { motion } from "framer-motion";
import { Check, Play } from "lucide-react";

// Custom Counter Component
const Counter = ({ end }) => {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (count < end) {
        setCount(count + 1);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [count, end]);

  return (
    <motion.div className="text-xl font-semibold">
      {count}+
    </motion.div>
  );
};

const features = [
  "Headquartered in RAK-UAE with a strong presence across Africa, the Middle East, Subcontinent, and South East Asia.",
  "Trusted partner for specialty chemicals across industries, including Coatings, Plastics, Pharmaceuticals, and more.",
  "Comprehensive solutions with technical expertise, logistics support, and tailored services for manufacturers worldwide.",
];

export function ExperienceSection() {
  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-left mb-12 md:mb-16">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="text-blue-600 font-medium block mb-4"
              >
                Superior Experience
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl md:text-5xl font-bold leading-none"
              >
                Unmatched Expertise in Chemicals
              </motion.h2>
            </div>

            <p className="text-gray-600 mb-8 text-lg text-left">
              We are dedicated to providing reliable Chemicals, guaranteeing the highest
              standards of trustworthiness and excellence.
              Over years of excellence in industrial chemical distribution with a global footprint.
            </p>

            <div className="space-y-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="flex items-start gap-3"
                >
                  <div className="bg-blue-600 rounded-full p-1 mt-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="/images/pexels-fauxels-3183197.jpg"
                alt="Medical Professional"
                className="object-cover size-full"
              />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-8 left-8 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3"
              >
                <div className="bg-blue-100 rounded-full p-2">
                  <img
                    src="/images/all_products.jpg"
                    alt="Award Icon"
                    className="text-blue-600 size-6 object-cover"
                  />
                </div>
                <div>
                  <Counter end={50} />
                  <p className="text-sm text-gray-600">Our Products</p>
                </div>
              </motion.div>
              <div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="absolute top-0 right-0 bg-white rounded-bl-full w-[calc(100%-56%)] h-28 flex items-center justify-center shadow-lg"
              >
                <div className="relative w-full h-full">
                  <div className="absolute inset-0 animate-spin-slow">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <path
                        id="circle"
                        d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                        fill="none"
                        stroke="none"
                      />
                      <text fontSize="10">
                        <textPath href="#circle">
                          Trustworthy • Reliable •
                        </textPath>
                      </text>
                    </svg>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-blue-600 rounded-full p-2">
                      <Play className="w-4 h-4 text-white" fill="currentColor" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
